/**
 * Search translations — expands Hindi/English grocery words in store search.
 *
 * Reads synonym groups from <script type="application/json" id="search-synonyms">
 * (Theme settings > Store info > Search translations). When any search form
 * (predictive search or the search page) sends its `q` value, a word that belongs
 * to a group is expanded with Shopify's OR search syntax, so "zeera powder" also
 * finds "jeera powder" and "cumin powder". The shopper's input is never changed;
 * only the request is. On the results page the original words are put back in
 * the search box.
 */
const STORAGE_KEY = 'search-synonyms:originals';
const MAX_ORIGINALS = 20;

function loadGroups() {
  const el = document.getElementById('search-synonyms');
  if (!el) return new Map();
  let groups = [];
  try {
    groups = JSON.parse(el.textContent || '[]');
  } catch {
    return new Map();
  }
  const lookup = new Map();
  for (const group of groups) {
    const words = group.map((word) => String(word).trim().toLowerCase()).filter(Boolean);
    if (words.length < 2) continue;
    for (const word of words) {
      if (!lookup.has(word)) lookup.set(word, words);
    }
  }
  return lookup;
}

const lookup = loadGroups();

/** Leave queries that already use search syntax alone. */
function usesSyntax(query) {
  return /["():\-*]|\s(OR|AND|NOT)\s/.test(query);
}

export function expandQuery(query) {
  const trimmed = query.trim();
  if (!trimmed || lookup.size === 0 || usesSyntax(trimmed)) return trimmed;

  const words = trimmed.split(/\s+/);
  const index = words.findIndex((word) => lookup.has(word.toLowerCase()));
  if (index === -1) return trimmed;

  const typed = words[index].toLowerCase();
  const alternatives = [typed, ...lookup.get(typed).filter((word) => word !== typed)];
  const rest = words.filter((_, i) => i !== index);
  // Other words first so they apply to every alternative: "powder zeera OR jeera OR cumin".
  const prefix = rest.length ? `${rest.join(' ')} ` : '';
  return `${prefix}${alternatives.join(' OR ')}`;
}

function readOriginals() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function rememberOriginal(expanded, original) {
  try {
    const originals = readOriginals();
    originals[expanded] = original;
    const keys = Object.keys(originals);
    if (keys.length > MAX_ORIGINALS) delete originals[keys[0]];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(originals));
  } catch {
    // Storage can be unavailable (private mode); expansion still works.
  }
}

function isSearchForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  try {
    return new URL(form.action, window.location.href).pathname.endsWith('/search');
  } catch {
    return false;
  }
}

// Fires for native submits and for every `new FormData(form)` (live search updates).
document.addEventListener(
  'formdata',
  (event) => {
    if (!isSearchForm(event.target)) return;
    const original = event.formData.get('q');
    if (typeof original !== 'string') return;
    const expanded = expandQuery(original);
    if (expanded === original.trim()) return;
    event.formData.set('q', expanded);
    rememberOriginal(expanded, original.trim());
  },
  true,
);

/** Show the words the shopper typed, not the expanded query, in search boxes. */
function restoreInputs() {
  const params = new URLSearchParams(window.location.search);
  const q = params.get('q');
  if (!q || !q.includes(' OR ')) return;
  const original = readOriginals()[q];
  if (!original) return;
  for (const input of document.querySelectorAll('input[name="q"]')) {
    if (input.value === q) input.value = original;
  }
}

// Run after other components have restored their values from the URL.
window.requestAnimationFrame(() => window.requestAnimationFrame(restoreInputs));
